import React, { FC } from "react";
import { Alert, Box, ListItemText, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { AnalysisParameters } from "../../../../hooks";
import {
  REGRESSION_OPTIONS,
  getRegressionParametersByLang,
  getRegressionDropdownLabel,
  getRegressionOptionPrefix,
} from "../../../../hooks/regressionOptions";

import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@components/base";

interface StandardParametersFormProps {
  parameters: AnalysisParameters;
  onParameterChange: (
    field: keyof AnalysisParameters,
    value: string | number,
  ) => void;
  currencies: Array<{ id: string; alphabeticCode: string }> | undefined;
  currenciesEnabled: boolean;
  onSave: () => void;
  showSaveSuccess: boolean;
}

export const StandardParametersForm: FC<StandardParametersFormProps> = ({
  parameters,
  onParameterChange,
  currencies,
  currenciesEnabled,
  onSave,
  showSaveSuccess,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
      <TextField
        label={t("paygap.parameters.referenceYear")}
        type="number"
        value={parameters.referenceYear}
        onChange={(e) => onParameterChange("referenceYear", e.target.value)}
        fullWidth
        inputProps={{ min: 2020, max: 2030 }}
      />

      <TextField
        label={t("paygap.parameters.workweek")}
        type="text"
        inputMode="decimal"
        value={parameters.standardWorkweek}
        onChange={(e) => {
          const normalizedValue = e.target.value.replace(",", ".");

          onParameterChange("standardWorkweek", normalizedValue);
        }}
        fullWidth
        inputProps={{ step: 0.1, pattern: "[0-9]*[.,]?[0-9]*" }}
      />

      <FormControl fullWidth>
        <InputLabel>{t("paygap.parameters.ageFormat")}</InputLabel>
        <Select
          value={parameters.ageFormat}
          onChange={(e) => onParameterChange("ageFormat", e.target.value as string)}
        >
          <MenuItem value="age_years">{t("paygap.parameters.ageYears")}</MenuItem>
          <MenuItem value="birth_year">{t("paygap.parameters.birthYear")}</MenuItem>
          <MenuItem value="birth_date">{t("paygap.parameters.birthDate")}</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>{t("paygap.parameters.serviceFormat")}</InputLabel>
        <Select
          value={parameters.serviceFormat}
          onChange={(e) =>
            onParameterChange("serviceFormat", e.target.value as string)
          }
        >
          <MenuItem value="years">{t("paygap.parameters.years")}</MenuItem>
          <MenuItem value="entry_year">{t("paygap.parameters.entryYear")}</MenuItem>
          <MenuItem value="entry_date">{t("paygap.parameters.entryDate")}</MenuItem>
        </Select>
      </FormControl>

      {currenciesEnabled && (
        <FormControl fullWidth>
          <InputLabel>{t("paygap.parameters.currency")}</InputLabel>
          <Select
            value={parameters.currency}
            onChange={(e) => onParameterChange("currency", e.target.value as string)}
          >
            {currencies?.map((currency) => (
              <MenuItem key={currency.id} value={currency.alphabeticCode}>
                {currency.alphabeticCode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth sx={{ gridColumn: "1 / -1" }}>
        <InputLabel>
          {t("paygap.parameters.regressionParameters", getRegressionDropdownLabel(i18n.language))}
        </InputLabel>
        <Select
          value={parameters.regression || "1"}
          onChange={(e) => onParameterChange("regression", e.target.value as string)}
          renderValue={(selected) => {
            const prefix = t(
              "paygap.parameters.regressionOption",
              getRegressionOptionPrefix(i18n.language),
            );

            return `${prefix} ${selected} – ${getRegressionParametersByLang(String(selected), i18n.language)}`;
          }}
        >
          {REGRESSION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {`${t("paygap.parameters.regressionOption", getRegressionOptionPrefix(i18n.language))} ${option.value}`}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {getRegressionParametersByLang(option.value, i18n.language)}
                  </Typography>
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box gridColumn="1 / -1" display="flex" flexDirection="column" gap={2} mt={2}>
        {showSaveSuccess && (
          <Alert severity="success">
            {t("organizationSettings.settingsSaved")}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={onSave}>
            {t("paygap.referentials.save")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
